import React, {
  Component,
  Fragment,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { CircularProgress } from "@mui/material";
import { compose } from "recompose";
import styles2, { getPHListStyle, getDDItemStyle } from "../styles";
import styles from "./styles";
import "./styles.scss";
import "views/Pma/PmaEditor/CommonEditor/ivyprestoFonts.scss";
import { Dashboard as DashboardLayout } from "layouts";
import { withSnackbar } from "notistack";
import { Portlet } from "components";
import { PortletContent } from "components";
import { PortletHeader } from "components";
import classNames from "classnames";
import { ImageUploadDialog } from "components";
import { GetAreaPropHighlights } from "services/others";
import Base from "common/constants";
import { GetStateStorage, IsValidSessionSwitch } from "common/storage";
import moment from "moment";
import { GetPmaAdminDataAPI } from "services/agent";
import {
  UpdatePropertyHighlights,
  PropertyHighlightPhotoOptimization,
  UpdatePropertyHighlightsForSingleArea,
} from "services/agent";
import { showErrorNotification, showInfoNotification } from "common/helpers";
import { showSuccessNotification } from "common/helpers";
import { useParams, useNavigate } from "react-router-dom";
import { Store } from "Store";
import { MediaViewComponent } from "components";
import DocumentTitle from "react-document-title";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";
import { propertyUtil } from "views/Pma/PropertyUtil";
import HighlightRow from "./HighlightRow";
import PropHighlightsEditProperty from "./PropHighlightsEditProperty";
import PropHighlightsAddProperty from "./PropHighlightsAddProperty";
import YETwoPropertyHighlights from "views/Pma/PmaEditor/FourthPageComponents/PropertyHighlights";
import ErrorModal from "./ErrorModal";
import BuyerPermissionModal from "./BuyerPermissionModal";
import HighlightTitleDialog from "./HighlightTitleDialog";
import HighlightFooterDialog from "./HighlightFooterDialog";
import { isUserAdmin } from "common/roles/role";
require("../../Pma/PmaEditor/YearEnd/YETwo/ye2styles.scss");

const getPropertyDedupeKey = (prop) => {
  if (prop.listingID) {
    return `listing-${prop.listingID}`;
  }
  const streetNumber = String(prop.streetNumber ?? "")
    .trim()
    .toLowerCase();
  const streetName = String(prop.streetName ?? "")
    .trim()
    .toLowerCase();
  const unitNumber = String(prop.unitNumber ?? "")
    .trim()
    .toLowerCase();
  return `addr-${streetNumber}-${streetName}-${unitNumber}`;
};

const mergeAvailablePhotoDetails = (photoDetails, buyerAgentPhotoDetails) => {
  const seen = new Set();
  const merged = [];

  (photoDetails || []).forEach((prop) => {
    const key = getPropertyDedupeKey(prop);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(prop);
  });

  (buyerAgentPhotoDetails || []).forEach((prop) => {
    const key = getPropertyDedupeKey(prop);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push({
      ...prop,
      isBuyingAgent: true,
    });
  });

  return merged;
};

const isBuyerRepresentedProperty = (property) =>
  property.isBuyingAgent === true;

// SortableItem component for @dnd-kit
const SortableItem = ({
  prop,
  index,
  areaID,
  handleSelect,
  openEditDialogFn,
  showButton,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id:
      prop.dragDropId ||
      `${prop.streetNumber || ""}-${prop.streetName || ""}-${
        prop.unitNumber || "-"
      }-${prop.photoURL || prop.optimizedPhotoURL || ""}-${
        prop.listingID || ""
      }`.replace(/\s+/g, "-"),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="highlight-row-container"
      {...attributes}
      {...listeners}
    >
      <HighlightRow
        prop={prop}
        index={index}
        areaID={areaID}
        handleSelect={handleSelect}
        openEditDialogFn={openEditDialogFn}
        showButton={showButton}
        isSelectedTable={true}
      />
    </div>
  );
};

// SortablePhotoWrapper component for making photos draggable
const SortablePhotoWrapper = ({ prop, children, activeId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id:
      prop.dragDropId ||
      `${prop.streetNumber || ""}-${prop.streetName || ""}-${
        prop.unitNumber || "-"
      }-${prop.photoURL || prop.optimizedPhotoURL || ""}-${
        prop.listingID || ""
      }`.replace(/\s+/g, "-"),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: activeId !== null ? "grabbing" : "grab",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-photo-wrapper"
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};
const PropertyHighlights = (props) => {
  //adding comment
  const { classes } = props;
  const { dispatch } = useContext(Store);
  const { areaID: routeAreaID } = useParams();
  const navigate = useNavigate();
  const isAValidSessionSwitch = IsValidSessionSwitch();
  const { userData } = GetStateStorage() || {};

  // Configure sensors for @dnd-kit
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before dragging starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Track dragging state for visual feedback
  const [activeId, setActiveId] = useState(null);
  const regexWithPDF = new RegExp(/^.*\.(PDF|pdf)$/);
  const maxHighlightTitleLength = 45;
  const maxHighlightSubtitleLength = 215;
  const maxHighlightFooter1Length = 70; //60
  const maxHighlightFooter2Length = 30;
  const maxPropertiesNum = 4;

  const [showEditDialog, setEditDialog] = React.useState(false);
  const [showAddPropDialog, setAddPropDialog] = React.useState(false);
  const [showHighlightTitleDialog, setHighlightTitleDialog] =
    React.useState(false);
  const [showHighlightFooterDialog, setHighlightFooterDialog] =
    React.useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showBuyerPermissionModal, setShowBuyerPermissionModal] =
    useState(false);
  const [pendingBuyerProperty, setPendingBuyerProperty] = useState(null);
  const [errorModalText, setErrorModalText] = useState("");
  const [missingFields, setMissingFields] = useState([]);
  const [goToPma, setGoToPma] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [singleAreaSubmit, setSingleAreaSubmit] = useState(false);
  const [hasClickedSaveOrBack, setHasClickedSaveOrBack] = useState(false);
  const [openUploadImageDialog, setUploadImageDialog] = React.useState(false);
  const [openViewImageDialog, setViewImageDialog] = React.useState(false);
  const [uploadImageKey, setUploadImageKey] = React.useState(null);
  const [uploadCurrentImage, setUploadCurrentImage] = React.useState(null);
  const [oldTitle, setOldTitle] = React.useState({
    propertyHighlightsTitle: "",
    propertyHighlightsSubtitle: "",
  });
  const [oldFooter, setOldFooter] = React.useState({
    propertyHighlightsFooterText1: "",
    propertyHighlightsFooterText2: "",
  });
  const [editPropFormData, setEditPropFormData] = React.useState({
    address: "",
    bedBaths: "",
    brokerageName: "",
    cityName: "",
    daysOnMarket: "",
    displayOrder: "",
    isBuyingAgent: false,
    isAgentSelected: null,
    isSystemSelected: null,
    isSellingAgent: true,
    listDate: "",
    listPrice: "",
    listingAgentLicenseID: "",
    listingAgentName: "",
    lotSizeSquareFt: "",
    photoURL: "",
    optimizedPhotoURL: "",
    saleDate: "",
    salePrice: "",
    sellingAgentLicenseID: "",
    sellingAgentName: "",
    squareFeet: "",
    status: "",
    streetDirPrefix: "-",
    streetName: "",
    streetNumber: "",
    streetSuffix: "",
    unitNumber: "-",
    view: "",
    cityName: "",
    propertyType: null,
    coListingAgents: null,
    coSellingAgents: null,
    coListingAgentLastName: null,
    coListingAgentFirstName: null,
    coSellingAgentFirstName: null,
    coSellingAgentLastName: null,
    guestHouseBathsAndBeds: null,
    isAgentFiltered: null,
    parcelNumber: null,
    listingID: "",
    originalListPrice: null,
    justAdded: false,
    bannerText1: "",
    bannerText2: "",
    footerText1: "",
    footerText2: null,
    footerText3: null,
    courtesyText1: "",
  });

  const [oldPropData, setOldPropData] = React.useState({
    address: "",
    bedBaths: "",
    brokerageName: "",
    cityName: "",
    daysOnMarket: "",
    displayOrder: "",
    isBuyingAgent: false,
    isAgentSelected: null,
    isSystemSelected: null,
    isSellingAgent: true,
    listDate: "",
    listPrice: "",
    listingAgentLicenseID: "",
    listingAgentName: "",
    lotSizeSquareFt: "",
    photoURL: "",
    optimizedPhotoURL: "",
    saleDate: "",
    salePrice: "",
    sellingAgentLicenseID: "",
    sellingAgentName: "",
    squareFeet: "",
    status: "",
    streetDirPrefix: "-",
    streetName: "",
    streetNumber: "",
    streetSuffix: "",
    unitNumber: "-",
    view: "",
    cityName: "",
    propertyType: null,
    coListingAgents: null,
    coSellingAgents: null,
    coListingAgentLastName: null,
    coListingAgentFirstName: null,
    coSellingAgentFirstName: null,
    coSellingAgentLastName: null,
    guestHouseBathsAndBeds: null,
    isAgentFiltered: null,
    parcelNumber: null,
    listingID: "",
    originalListPrice: null,
    justAdded: false,
    bannerText1: "",
    bannerText2: "",
    footerText1: "",
    footerText2: null,
    footerText3: null,
    courtesyText1: "",
  });
  const emptyProp = {
    address: "",
    bedBaths: "",
    brokerageName: "",
    cityName: "",
    daysOnMarket: "",
    displayOrder: "",
    isBuyingAgent: false,
    isAgentSelected: null,
    isSystemSelected: null,
    isSellingAgent: true,
    listDate: "",
    listPrice: "",
    listingAgentLicenseID: "",
    listingAgentName: "",
    lotSizeSquareFt: "",
    photoURL: "",
    optimizedPhotoURL: "",
    saleDate: "",
    salePrice: "",
    sellingAgentLicenseID: "",
    sellingAgentName: "",
    squareFeet: "",
    status: "",
    streetDirPrefix: "-",
    streetName: "",
    streetNumber: "",
    streetSuffix: "",
    unitNumber: "-",
    view: "",
    cityName: "",
    propertyType: null,
    coListingAgents: null,
    coSellingAgents: null,
    coListingAgentLastName: null,
    coListingAgentFirstName: null,
    coSellingAgentFirstName: null,
    coSellingAgentLastName: null,
    guestHouseBathsAndBeds: null,
    isAgentFiltered: null,
    parcelNumber: null,
    listingID: "",
    originalListPrice: null,
    justAdded: false,
    bannerText1: "",
    bannerText2: "",
    footerText1: "",
    footerText2: null,
    footerText3: null,
    courtesyText1: "",
  };
  const [propHighlightData, setPropHighlightData] = React.useState([]);
  const [agentSelectedPhotoDetails, setAgentSelectedPhotoDetails] =
    React.useState([]);
  const [originalSelectedPhotoDetails, setOriginalSelectedPhotoDetails] =
    React.useState([]);
  const [originalTitleFooterData, setOriginalTitleFooterData] = React.useState({
    propertyHighlightsTitle: "",
    propertyHighlightsSubtitle: "",
    propertyHighlightsFooterText1: "",
    propertyHighlightsFooterText2: "",
  });
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const isAdminAccess =
    isAValidSessionSwitch || isUserAdmin(userData && userData.listgenRoleID);

  let agentSelectedPhotoDetailsPropExample = {
    address: "314 Wood ST",
    bedBaths: "2/1",
    brokerageName: "Sotheby's International Realty",
    daysOnMarket: "4",
    displayOrder: 0,
    isBuyingAgent: false,
    isAgentSelected: null,
    isSystemSelected: null,
    isSellingAgent: true,
    listDate: "2024-04-29T12:22:18",
    listingAgentLicenseID: "01431994",
    listingAgentName: "Kyle Morrison",
    listPrice: "1549000",
    lotSizeSquareFt: "2700.00",
    photoURL:
      "https://media.mlslmedia.com/property/MLSL/81963434/8b76c90260274eb89f0fe90d3bc4ae8f/2/1",
    optimizedPhotoURL:
      "https://featuredpropertyphotos.s3.amazonaws.com/129-ML81963434.jpg",
    saleDate: "2024-05-17T00:00:00",
    salePrice: "1575000",
    sellingAgentLicenseID: "70010222",
    sellingAgentName: "TruszkowskiFreedman",
    squareFeet: "1084",
    status: "Sold",
    streetDirPrefix: "-",
    streetName: "Wood",
    streetNumber: "314",
    streetSuffix: "ST",
    unitNumber: "-",
    view: "Neighborhood",
    cityName: "Carmel Valley",
    propertyType: null,
    coListingAgents: null,
    coSellingAgents: null,
    coListingAgentLastName: null,
    coListingAgentFirstName: null,
    coSellingAgentFirstName: null,
    coSellingAgentLastName: null,
    guestHouseBathsAndBeds: null,
    isAgentFiltered: null,
    parcelNumber: null,
    listingID: "ML81963434",
    originalListPrice: null,
    bannerText1: "314 Wood ST",
    bannerText2: "Sold for 1,575,000",
    footerText1: "I Represented the Seller",
    footerText2: null,
    footerText3: null,
    courtesyText1: "Image courtesy of Sotheby's International Realty",
  };
  const isValidTopFiveForm = () => {
    let isValid = true;
    const {
      areaID,
      countyID,
      listgenUserAreaID,
      listgenUserID,
      photoDetails,
      photoDetailsRunDate,
    } = propHighlightData;

    console.log(
      "isValidTopFiveForm check - propHighlightData:",
      propHighlightData,
    );
    console.log("areaID:", areaID);
    console.log("countyID:", countyID);
    console.log("listgenUserAreaID:", listgenUserAreaID);
    console.log("listgenUserID:", listgenUserID);
    console.log("photoDetails:", photoDetails);
    console.log("photoDetailsRunDate:", photoDetailsRunDate);

    if (
      !areaID ||
      !countyID ||
      !listgenUserAreaID ||
      !listgenUserID ||
      photoDetails === null ||
      photoDetails === undefined ||
      !photoDetailsRunDate
    ) {
      console.log("***false valid - missing required fields");
      isValid = false;
    }
    console.log("is Valid====", isValid);
    return isValid;
  };

  const hasSelectedPropertiesChanged = () => {
    const current = agentSelectedPhotoDetails || [];
    const original = originalSelectedPhotoDetails || [];

    if (current.length !== original.length) {
      return true;
    }

    for (let i = 0; i < current.length; i++) {
      const currentProp = current[i];
      const originalProp = original[i];

      if (
        currentProp.bannerText1 !== originalProp.bannerText1 ||
        currentProp.bannerText2 !== originalProp.bannerText2 ||
        currentProp.status !== originalProp.status ||
        currentProp.buyerRepresentedReason !==
          originalProp.buyerRepresentedReason ||
        currentProp.checkedBuyerRepresented !==
          originalProp.checkedBuyerRepresented ||
        currentProp.courtesyText1 !== originalProp.courtesyText1 ||
        currentProp.footerText1 !== originalProp.footerText1 ||
        currentProp.footerText2 !== originalProp.footerText2 ||
        currentProp.listPrice !== originalProp.listPrice ||
        currentProp.optimizedPhotoURL !== originalProp.optimizedPhotoURL ||
        currentProp.photoURL !== originalProp.photoURL ||
        currentProp.salePrice !== originalProp.salePrice ||
        currentProp.displayOrder !== originalProp.displayOrder
      ) {
        return true;
      }
    }

    return false;
  };

  const hasTitleFooterChanged = () => {
    const {
      propertyHighlightsTitle,
      propertyHighlightsSubtitle,
      propertyHighlightsFooterText1,
      propertyHighlightsFooterText2,
    } = propHighlightData;

    return (
      (propertyHighlightsTitle ?? "") !==
        (originalTitleFooterData.propertyHighlightsTitle ?? "") ||
      (propertyHighlightsSubtitle ?? "") !==
        (originalTitleFooterData.propertyHighlightsSubtitle ?? "") ||
      (propertyHighlightsFooterText1 ?? "") !==
        (originalTitleFooterData.propertyHighlightsFooterText1 ?? "") ||
      (propertyHighlightsFooterText2 ?? "") !==
        (originalTitleFooterData.propertyHighlightsFooterText2 ?? "")
    );
  };

  const getIsTitleFooterEditedForSubmit = () => {
    if (propHighlightData.isTitleFooterEdited === true) {
      return true;
    }
    if (hasTitleFooterChanged()) {
      return true;
    }
    return null;
  };

  const buildSubmitData = () => {
    const { photoDetails } = propHighlightData;
    const selected = agentSelectedPhotoDetails || [];

    return {
      ...propHighlightData,
      photoDetails: photoDetails || [],
      agentSelectedPhotoDetails: selected,
      propertyHighlightsTitle: propHighlightData.propertyHighlightsTitle ?? "",
      propertyHighlightsSubtitle:
        propHighlightData.propertyHighlightsSubtitle ?? "",
      propertyHighlightsFooterText1:
        propHighlightData.propertyHighlightsFooterText1 ?? "",
      propertyHighlightsFooterText2:
        propHighlightData.propertyHighlightsFooterText2 ?? "",
      isAgentEdited:
        propHighlightData.isAgentEdited === true ||
        hasSelectedPropertiesChanged(),
      isTitleFooterEdited: getIsTitleFooterEditedForSubmit(),
    };
  };

  const onPropHighlightSubmit = useCallback(
    (useSingleAreaSubmit) => {
      console.log("inside onPropHighlightSubmit");

      let isValid = isValidTopFiveForm();
      if (!isValid) {
        console.log("issue with one area submit");
        setErrorModalText("Issue submitting form");
        setShowErrorModal(true);
        setIsSubmitting(false);
      } else {
        console.log("inside else one area");
        try {
          console.log("inside try one area");
          setBtnLoading(true);
          let submitData = buildSubmitData();
          if (singleAreaSubmit || useSingleAreaSubmit) {
            UpdatePropertyHighlightsForSingleArea(submitData)
              .then((data) => {
                showSuccessNotification(
                  props,
                  `Your property highlights have been saved`,
                );
                setBtnLoading(false);
                setIsSubmitting(false);
                if (goToPma) {
                  setTimeout(() => {
                    navigate(`/pma/${routeAreaID}`);
                  }, 1300);
                }
              })
              .catch((err) => {
                console.log("issue processing request", err);
                showErrorNotification(
                  props,
                  "Something went wrong while processing your request.",
                );
                setBtnLoading(false);
                setIsSubmitting(false);
              });
          } else {
            UpdatePropertyHighlights(submitData)
              .then((data) => {
                showSuccessNotification(
                  props,
                  `Your property highlights have been saved`,
                );
                setBtnLoading(false);
                setIsSubmitting(false);
                if (goToPma) {
                  setTimeout(() => {
                    navigate(`/pma/${routeAreaID}`);
                  }, 1300);
                }
              })
              .catch((err) => {
                showErrorNotification(
                  props,
                  "Something went wrong while processing your request.",
                );
                setBtnLoading(false);
                setIsSubmitting(false);
              });
          }
        } catch (err) {
          console.log("issue processing request", err);
          showErrorNotification(props, "Unable to process request.");
          setBtnLoading(false);
          setIsSubmitting(false);
        }
      }
    },
    [
      propHighlightData,
      agentSelectedPhotoDetails,
      originalSelectedPhotoDetails,
      originalTitleFooterData,
      isValidTopFiveForm,
      goToPma,
      props,
      singleAreaSubmit,
      routeAreaID,
      navigate,
    ],
  );
  const getAreaPropHighlights = () => {
    setIsLoading(true);
    const thisArea = routeAreaID;
    GetAreaPropHighlights(userData && userData.listgenUserID, thisArea)
      .then((data) => {
        if (Object.keys(data).length > 0) {
          const {
            photoDetails,
            buyerAgentPhotoDetails,
            agentSelectedPhotoDetails,
            propertyHighlightsTitle,
            propertyHighlightsSubtitle,
          } = data;

          const mergedPhotoDetails = mergeAvailablePhotoDetails(
            photoDetails,
            buyerAgentPhotoDetails,
          );

          let updatedData = {
            ...data,
            isAgentEdited: data.isAgentEdited === true,
            isTitleFooterEdited:
              data.isTitleFooterEdited === true ? true : null,
            photoDetails: mergedPhotoDetails,
            propertyHighlightsTitle: propertyHighlightsTitle ?? "",
            propertyHighlightsSubtitle: propertyHighlightsSubtitle ?? "",
            propertyHighlightsFooterText1: data.propertyHighlightsFooterText1 ?? "",
            propertyHighlightsFooterText2: data.propertyHighlightsFooterText2 ?? "",
          };
          setPropHighlightData(updatedData);
          setOriginalTitleFooterData({
            propertyHighlightsTitle: updatedData.propertyHighlightsTitle ?? "",
            propertyHighlightsSubtitle:
              updatedData.propertyHighlightsSubtitle ?? "",
            propertyHighlightsFooterText1:
              updatedData.propertyHighlightsFooterText1 ?? "",
            propertyHighlightsFooterText2:
              updatedData.propertyHighlightsFooterText2 ?? "",
          });
          // Ensure all properties have a stable dragDropId for drag and drop functionality
          // Use stable properties (not index) to create unique IDs that won't change on reorder
          const agentSelectedWithIds = (agentSelectedPhotoDetails || []).map(
            (prop) => {
              // If dragDropId already exists, keep it. Otherwise, create a stable one based on property data
              if (prop.dragDropId) {
                return prop;
              }
              // Create a stable ID based on property characteristics (not index)
              const stableId = `${prop.streetNumber || ""}-${
                prop.streetName || ""
              }-${prop.unitNumber || "-"}-${
                prop.photoURL || prop.optimizedPhotoURL || ""
              }-${prop.listingID || ""}`.replace(/\s+/g, "-");
              return {
                ...prop,
                dragDropId: stableId,
              };
            },
          );
          setAgentSelectedPhotoDetails(agentSelectedWithIds);
          setOriginalSelectedPhotoDetails(agentSelectedWithIds);
          setHasClickedSaveOrBack(false);
          setEditPropFormData({
            ...editPropFormData,
          });
        } else {
          setPropHighlightData([]);
          setAgentSelectedPhotoDetails([]);
          setOriginalSelectedPhotoDetails([]);
          setOriginalTitleFooterData({
            propertyHighlightsTitle: "",
            propertyHighlightsSubtitle: "",
            propertyHighlightsFooterText1: "",
            propertyHighlightsFooterText2: "",
          });
        }
      })
      .catch((err) => {
        setPropHighlightData([]);
        setAgentSelectedPhotoDetails([]);
        setOriginalSelectedPhotoDetails([]);
        setOriginalTitleFooterData({
          propertyHighlightsTitle: "",
          propertyHighlightsSubtitle: "",
          propertyHighlightsFooterText1: "",
          propertyHighlightsFooterText2: "",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    console.log(
      "useEffect triggered - isSubmitting:",
      isSubmitting,
      "propHighlightData keys:",
      Object.keys(propHighlightData).length,
      "singleAreaSubmit:",
      singleAreaSubmit,
    );
    if (
      propHighlightData &&
      Object.keys(propHighlightData).length > 0 &&
      isSubmitting === true
    ) {
      console.log(
        "Calling onPropHighlightSubmit with singleAreaSubmit:",
        singleAreaSubmit,
      );
      onPropHighlightSubmit(singleAreaSubmit);
    }
  }, [
    isSubmitting,
    onPropHighlightSubmit,
    propHighlightData,
    singleAreaSubmit,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (Object.keys(propHighlightData).length === 0) {
      getAreaPropHighlights();
    }
  }, []);

  const openUploadMedia = async (key, currentImage) => {
    setUploadImageDialog(true);
    setUploadCurrentImage(currentImage);
    setUploadImageKey(key);
  };

  const handlePropertyHighlightUpload = (uploadedImageUrl) => {
    if (uploadedImageUrl) {
      let changedObj = {
        ...editPropFormData,
        photoURL: uploadedImageUrl.optimizedPhotoURL,
        optimizedPhotoURL: uploadedImageUrl.optimizedPhotoURL,
        justAdded: true,
      };
      setEditPropFormData(changedObj);
    } else {
      console.log("having an issue");
    }
    // if (uploadedImageUrl) {
    //   console.log("**changedObj", changedObj);
    //   setEditPropFormData(changedObj);
    //   setEditPropFormData({
    //     ...editPropFormData,
    //     photoURL: uploadedImageUrl.optimizedPhotoURL,
    //     optimizedPhotoURL: uploadedImageUrl.optimizedPhotoURL,
    //     justAdded: true,
    //   });
    // } else {
    //   console.log("having an issue");
    // }

    setUploadImageDialog(false);
    setUploadCurrentImage(null);
    setUploadImageKey(null);
    setShowError("");
  };

  const openViewMedia = async (currentImage) => {
    setViewImageDialog(true);
    setUploadCurrentImage(currentImage);
  };

  const closeViewMedia = async () => {
    setViewImageDialog(false);
    setUploadCurrentImage(null);
  };

  const handleOpenEditDialog = (prop) => {
    const propObj = prop;
    setOldPropData({
      ...propObj,
      salePrice:
        propObj.salePrice === "-" || propObj.salePrice === "1"
          ? "0"
          : propObj.salePrice,
      listPrice:
        propObj.listPrice === "-" || propObj.listPrice === "1"
          ? "0"
          : propObj.listPrice,
    });
    setEditPropFormData({
      ...propObj,
      salePrice:
        propObj.salePrice === "-" || propObj.salePrice === "1"
          ? "0"
          : propObj.salePrice,
      listPrice:
        propObj.listPrice === "-" || propObj.listPrice === "1"
          ? "0"
          : propObj.listPrice,
      footerText2: propObj.footerText2 || "",
      footerText3: propObj.footerText3 || "",
      courtesyText1: propObj.courtesyText1 || "",
    });
    setEditDialog(true);
  };

  const handleErrorModal = (type) => {
    if (type === "close") {
      setShowErrorModal(false);
      setErrorModalText("false");
      setMissingFields([]);
    }
  };

  const handleFormClose = () => {
    setAddPropDialog(false);
    setEditDialog(false);
  };
  const onHighlightTitleClose = () => {
    setPropHighlightData({
      ...propHighlightData,
      propertyHighlightsTitle: oldTitle.propertyHighlightsTitle,
      propertyHighlightsSubtitle: oldTitle.propertyHighlightsSubtitle,
    });
    setHighlightTitleDialog(false);
    setOldTitle({
      propertyHighlightsTitle: "",
      propertyHighlightsSubtitle: "",
    });
  };
  const onHighlightTitleOpen = () => {
    setHighlightTitleDialog(true);
    setOldTitle({
      propertyHighlightsTitle: propHighlightData.propertyHighlightsTitle,
      propertyHighlightsSubtitle: propHighlightData.propertyHighlightsSubtitle,
    });
  };
  const onHighlightFooterClose = () => {
    setPropHighlightData({
      ...propHighlightData,
      propertyHighlightsFooterText1: oldFooter.propertyHighlightsFooterText1,
      propertyHighlightsFooterText2: oldFooter.propertyHighlightsFooterText2,
    });
    setHighlightFooterDialog(false);
    setOldFooter({
      propertyHighlightsFooterText1: "",
      propertyHighlightsFooterText2: "",
    });
  };
  const onHighlightFooterOpen = () => {
    setHighlightFooterDialog(true);
    setOldFooter({
      propertyHighlightsFooterText1:
        propHighlightData.propertyHighlightsFooterText1,
      propertyHighlightsFooterText2:
        propHighlightData.propertyHighlightsFooterText2,
    });
  };
  const handleAddPropDialog = () => {
    let selected = agentSelectedPhotoDetails || [];
    if (selected.length >= maxPropertiesNum) {
      setErrorModalText("Max properties");
      setShowErrorModal(true);
    } else {
      setAddPropDialog(true);
      setEditPropFormData(emptyProp);
    }
  };

  const handleAddProperty = (oneAreaSubmit) => {
    const {
      bannerText1,
      bannerText2,
      footerText1,
      footerText2,
      courtesyText1,
      photoURL,
    } = editPropFormData;
    console.log("***ediutpropformdata", editPropFormData);

    // Only require courtesyText1 if property is buyer-represented
    const isBuyerRep =
      footerText1 &&
      (footerText1.includes("Buyer") || footerText1.includes("Buyers"));

    // Check permission type - courtesy text not required if "with-permission" or "own-rights"
    const buyerPermissionType =
      editPropFormData.buyerPermissionType ||
      editPropFormData.buyerRepresentedReason;
    const courtesyTextRequired =
      isBuyerRep &&
      buyerPermissionType !== "with-permission" &&
      buyerPermissionType !== "own-rights";

    if (
      !bannerText1 ||
      !bannerText2 ||
      !footerText1 ||
      (courtesyTextRequired && !courtesyText1) ||
      !photoURL
    ) {
      console.log("about to be missing inputs");
      const missing = [];
      if (!bannerText1) missing.push("Address");
      if (!bannerText2) missing.push("Status and Price");
      if (!footerText1) missing.push("Property Representation");
      if (courtesyTextRequired && !courtesyText1)
        missing.push("Photo Courtesy");
      if (!photoURL) missing.push("Photo");

      setMissingFields(missing);
      setErrorModalText("Missing inputs");
      setShowErrorModal(true);
    } else {
      let selected = agentSelectedPhotoDetails || [];
      let changedObj = {
        ...editPropFormData,
        displayOrder: selected.length,
      };
      setEditPropFormData(changedObj);
      setAgentSelectedPhotoDetails([
        ...(agentSelectedPhotoDetails || []),
        changedObj,
      ]);
      console.log("inside handle add property");
      setSingleAreaSubmit(oneAreaSubmit);
      setIsSubmitting(true);

      setAddPropDialog(false);
    }
  };
  const wholePageSubmit = (oneAreaSubmit) => {
    console.log("wholePageSubmit called with oneAreaSubmit:", oneAreaSubmit);
    setHasClickedSaveOrBack(true);
    setSingleAreaSubmit(oneAreaSubmit);
    setGoToPma(true);
    console.log(
      "wholePageSubmit - setSingleAreaSubmit to:",
      oneAreaSubmit,
    );

    // Direct submission without relying on useEffect or callback
    console.log("Starting direct submission...");

    let isValid = isValidTopFiveForm();
    console.log("Direct submission - isValid:", isValid);

    if (!isValid) {
      console.log("Direct submission - form not valid");
      setErrorModalText("Issue submitting form");
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }

    try {
      console.log("Direct submission - starting API call");
      setBtnLoading(true);
      let submitData = buildSubmitData();

      console.log("Direct submission - submitData:", submitData);
      console.log("Direct submission - oneAreaSubmit:", oneAreaSubmit);

      if (oneAreaSubmit) {
        console.log(
          "Direct submission - calling UpdatePropertyHighlightsForSingleArea",
        );
        UpdatePropertyHighlightsForSingleArea(submitData)
          .then((data) => {
            console.log("Direct submission - success:", data);
            showSuccessNotification(
              props,
              `Your property highlights have been saved`,
            );
            setBtnLoading(false);
            setIsSubmitting(false);
            setTimeout(() => {
              navigate(`/pma/${routeAreaID}`);
            }, 1300);
          })
          .catch((err) => {
            console.log("Direct submission - error:", err);
            if (err && err.response) {
              console.log("Direct submission - error response:", err.response);
              console.log("Direct submission - error data:", err.response.data);
              console.log(
                "Direct submission - error status:",
                err.response.status,
              );
            }
            if (err && err.message) {
              console.log("Direct submission - error message:", err.message);
            }
            showErrorNotification(
              props,
              "Something went wrong while processing your request.",
            );
            setBtnLoading(false);
            setIsSubmitting(false);
          });
      } else {
        console.log("Direct submission - calling UpdatePropertyHighlights");
        UpdatePropertyHighlights(submitData)
          .then((data) => {
            console.log("Direct submission - success:", data);
            showSuccessNotification(
              props,
              `Your property highlights have been saved`,
            );
            setBtnLoading(false);
            setIsSubmitting(false);
            setTimeout(() => {
              navigate(`/pma/${routeAreaID}`);
            }, 1300);
          })
          .catch((err) => {
            console.log("Direct submission - error:", err);
            if (err && err.response) {
              console.log("Direct submission - error response:", err.response);
              console.log("Direct submission - error data:", err.response.data);
              console.log(
                "Direct submission - error status:",
                err.response.status,
              );
            }
            if (err && err.message) {
              console.log("Direct submission - error message:", err.message);
            }
            showErrorNotification(
              props,
              "Something went wrong while processing your request.",
            );
            setBtnLoading(false);
            setIsSubmitting(false);
          });
      }
    } catch (err) {
      console.log("Direct submission - catch error:", err);
      showErrorNotification(props, "Unable to process request.");
      setBtnLoading(false);
      setIsSubmitting(false);
    }
  };
  const handleAddTitle = (overMaxTitle, overMaxSubtitle, oneAreaSubmit) => {
    if (overMaxTitle || overMaxSubtitle) {
      setErrorModalText("Max title");
      setShowErrorModal(true);
    } else {
      setSingleAreaSubmit(oneAreaSubmit);
      setIsSubmitting(true);
      setHighlightTitleDialog(false);
    }
  };

  const handleAddFooter = (overMaxFooter1, overMaxFooter2, oneAreaSubmit) => {
    if (overMaxFooter1 || overMaxFooter2) {
      setErrorModalText("Max title");
      setShowErrorModal(true);
    } else {
      setSingleAreaSubmit(oneAreaSubmit);
      setIsSubmitting(true);
      setHighlightFooterDialog(false);
    }
  };

  const onEditFormSubmit = (oneAreaSubmit) => {
    console.log("***EDITING PROPERTY oneAreaSubmit", oneAreaSubmit);
    console.log("**inside on edit form submit", editPropFormData);
    const {
      bannerText1,
      bannerText2,
      footerText1,
      footerText2,
      courtesyText1,
      photoURL,
    } = editPropFormData;

    // Only require courtesyText1 if property is buyer-represented
    const isBuyerRep =
      footerText1 &&
      (footerText1.includes("Buyer") || footerText1.includes("Buyers"));

    // Check permission type - courtesy text not required if "with-permission" or "own-rights"
    const buyerPermissionType =
      editPropFormData.buyerPermissionType ||
      editPropFormData.buyerRepresentedReason;
    const courtesyTextRequired =
      isBuyerRep &&
      buyerPermissionType !== "with-permission" &&
      buyerPermissionType !== "own-rights";

    if (
      !bannerText1 ||
      !bannerText2 ||
      !footerText1 ||
      (courtesyTextRequired && !courtesyText1) ||
      !photoURL
    ) {
      console.log("bannerText1", bannerText1);
      console.log("bannerText2", bannerText2);
      console.log("footerText1", footerText1);
      console.log("courtesyText1", courtesyText1);
      console.log("photoURL", photoURL);

      const missing = [];
      if (!bannerText1) missing.push("Address");
      if (!bannerText2) missing.push("Status and Price");
      if (!footerText1) missing.push("Property Representation");
      if (courtesyTextRequired && !courtesyText1)
        missing.push("Photo Courtesy");
      if (!photoURL) missing.push("Photo");

      setMissingFields(missing);
      setErrorModalText("Missing inputs");
      setShowErrorModal(true);
    } else {
      // Only update agentSelectedPhotoDetails, never photoDetails
      // Use oldPropData to identify which property to update (original values)
      const changePropertyInSelected = (agentSelectedPhotoDetails || []).map(
        (prop) =>
          prop.bannerText1 === oldPropData.bannerText1 &&
          prop.bannerText2 === oldPropData.bannerText2 &&
          prop.footerText1 === oldPropData.footerText1 &&
          prop.footerText2 === oldPropData.footerText2 &&
          prop.courtesyText1 === oldPropData.courtesyText1 &&
          prop.photoURL === oldPropData.photoURL
            ? editPropFormData
            : prop,
      );

      setSingleAreaSubmit(oneAreaSubmit);
      setAgentSelectedPhotoDetails(changePropertyInSelected || []);

      setIsSubmitting(true);
      handleFormClose();
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    // dropped outside the list
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = (agentSelectedPhotoDetails || []).findIndex(
      (prop) =>
        (prop.dragDropId ||
          `${prop.streetNumber || ""}-${prop.streetName || ""}-${
            prop.unitNumber || "-"
          }-${prop.photoURL || prop.optimizedPhotoURL || ""}-${
            prop.listingID || ""
          }`.replace(/\s+/g, "-")) === active.id,
    );
    const newIndex = (agentSelectedPhotoDetails || []).findIndex(
      (prop) =>
        (prop.dragDropId ||
          `${prop.streetNumber || ""}-${prop.streetName || ""}-${
            prop.unitNumber || "-"
          }-${prop.photoURL || prop.optimizedPhotoURL || ""}-${
            prop.listingID || ""
          }`.replace(/\s+/g, "-")) === over.id,
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const selected = arrayMove(
        agentSelectedPhotoDetails || [],
        oldIndex,
        newIndex,
      );
      // Update displayOrder
      const reordered = selected.map((prop, index) => ({
        ...prop,
        displayOrder: index,
      }));

      setAgentSelectedPhotoDetails(reordered);
      showInfoNotification(
        props,
        `Please SAVE YOUR CHANGES!  Click on one of the blue SUBMIT buttons at the top of the screen.`,
        20000,
        "bottom",
        "right",
      );
    }
  };

  const normalizeAddressPart = (value) => {
    const normalized = String(value ?? "").trim();
    if (normalized === "-" || normalized === "0") return "";
    return normalized.toLowerCase();
  };

  const hasAddressParts = (property) => {
    return (
      normalizeAddressPart(property.streetNumber) !== "" &&
      normalizeAddressPart(property.streetName) !== ""
    );
  };

  const isPropertySelectedByAddress = (property) => {
    if (!hasAddressParts(property)) return false;

    return (agentSelectedPhotoDetails || []).some(
      (selected) =>
        hasAddressParts(selected) &&
        normalizeAddressPart(selected.streetNumber) ===
          normalizeAddressPart(property.streetNumber) &&
        normalizeAddressPart(selected.streetName) ===
          normalizeAddressPart(property.streetName) &&
        normalizeAddressPart(selected.unitNumber) ===
          normalizeAddressPart(property.unitNumber),
    );
  };

  const addPropertyToSelection = (property) => {
    const isDuplicate = (agentSelectedPhotoDetails || []).some(
      (selected) => selected.bannerText1 === property.bannerText1,
    );

    if (isDuplicate) {
      setErrorModalText(
        "Cannot add duplicate property, please remove current selected property or add a property manually",
      );
      setShowErrorModal(true);
      return;
    }

    if ((agentSelectedPhotoDetails || []).length >= maxPropertiesNum) {
      setErrorModalText("Max properties");
      setShowErrorModal(true);
      return;
    }

    const stableId = `${property.streetNumber || ""}-${
      property.streetName || ""
    }-${property.unitNumber || "-"}-${
      property.photoURL || property.optimizedPhotoURL || ""
    }-${property.listingID || ""}`.replace(/\s+/g, "-");

    setAgentSelectedPhotoDetails([
      ...(agentSelectedPhotoDetails || []),
      {
        ...property,
        displayOrder: (agentSelectedPhotoDetails || []).length,
        dragDropId: property.dragDropId || stableId,
        bannerText1: property.bannerText1 || "",
        bannerText2: property.bannerText2 || "",
        footerText1: property.footerText1 || "",
        footerText2: property.footerText2 || "",
        footerText3: property.footerText3 || "",
        courtesyText1: property.courtesyText1 || "",
        ...(isBuyerRepresentedProperty(property)
          ? {
              isBuyingAgent: true,
              checkedBuyerRepresented: true,
              buyerRepresentedReason: "with-permission",
            }
          : {}),
      },
    ]);
  };

  const handleBuyerPermissionConfirm = () => {
    if (pendingBuyerProperty) {
      addPropertyToSelection(pendingBuyerProperty);
    }
    setPendingBuyerProperty(null);
    setShowBuyerPermissionModal(false);
  };

  const handleBuyerPermissionCancel = () => {
    setPendingBuyerProperty(null);
    setShowBuyerPermissionModal(false);
  };

  const handleSelect = (property, select) => {
    console.log(
      "handleSelect called with property:",
      property,
      "and select:",
      select,
    );
    if (select) {
      if (isBuyerRepresentedProperty(property)) {
        setPendingBuyerProperty(property);
        setShowBuyerPermissionModal(true);
        return;
      }

      addPropertyToSelection(property);
    } else {
      // Remove from selected
      const updatedSelected = (agentSelectedPhotoDetails || []).filter(
        (selected, idx) =>
          !(
            selected.bannerText1 === property.bannerText1 &&
            selected.bannerText2 === property.bannerText2 &&
            selected.footerText1 === property.footerText1 &&
            selected.courtesyText1 === property.courtesyText1 &&
            selected.photoURL === property.photoURL
          ),
      );
      // Reorder displayOrder
      const reordered = updatedSelected.map((prop, idx) => ({
        ...prop,
        displayOrder: idx,
      }));
      setAgentSelectedPhotoDetails(reordered);
    }
  };

  const handleInputChange = (update) => {
    if (update && update.target) {
      // Event: single field update
      const target = update.target;
      let value = target.value;
      const name = target.name;
      if (name === "salePrice" || name === "listPrice") {
        if (isNaN(value)) {
          return;
        }
        value = Number(value);
      }
      setEditPropFormData({
        ...editPropFormData,
        [name]: value,
      });
    } else if (typeof update === "object" && update !== null) {
      // Object: multi-field update
      setEditPropFormData((prev) => ({
        ...prev,
        ...update,
      }));
    }
  };

  const handleReplaceProperty = ({ isMemberOfTeam, brokerageName }) => {
    setEditPropFormData((prev) => ({
      ...prev,
      bannerText1: "",
      bannerText2: "",
      footerText2: "",
      footerText3: "",
      photoURL: "",
      optimizedPhotoURL: "",
      listPrice: "",
      salePrice: "",
      footerText1: `${isMemberOfTeam ? "we" : "I"} represented the Sellers`,
      courtesyText1: `Image courtesy of ${brokerageName}`,
    }));
  };

  const handleTitleChange = (e) => {
    const { name, value } = e.target;
    setPropHighlightData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFooterChange = (e) => {
    const { name, value } = e.target;
    setPropHighlightData((prev) => ({ ...prev, [name]: value }));
  };

  const radioSaleChange = (event) => {
    const { brokerageName } = userData;

    let val = event.target.value;
    if (val === "my-sale") {
      setEditPropFormData({
        ...editPropFormData,
        brokerageName,
        soldByOtherAgent: false,
      });
    } else {
      setEditPropFormData({
        ...editPropFormData,
        soldByOtherAgent: true,
      });
    }
  };

  const resetDisplayOrder = () => {
    let updatedSelectedList = (agentSelectedPhotoDetails || []).map(
      (property) => ({
        ...property,
        displayOrder: 0,
      }),
    );

    setAgentSelectedPhotoDetails(updatedSelectedList);
  };
  const scrollDownToProperties = () => {
    window.scrollTo({
      top: 970,
      behavior: "auto",
    });
  };
  const {
    photoDetails,
    areaID,
    propertyHighlightsTitle,
    propertyHighlightsSubtitle,
    propertyHighlightsFooterText1,
    propertyHighlightsFooterText2,
  } = propHighlightData;
  const { formatSuffix } = propertyUtil;

  let showButton = true;
  let showAllPropertiesButton = false;
  if (userData && userData.navbarInfo && userData.navbarInfo.length > 1) {
    showAllPropertiesButton = true;
  }
  const thisArea = routeAreaID;
  const unselectedPhotoDetails = (photoDetails || []).filter(
    (prop) => !isPropertySelectedByAddress(prop),
  );
  //inside highlights page

  return (
    <Fragment>
      {openViewImageDialog ? (
        <MediaViewComponent
          mediaURL={uploadCurrentImage}
          mediaTitle={"Property Highlight"}
          onOpen={() => {}}
          onClose={closeViewMedia}
        />
      ) : (
        ""
      )}
      {openUploadImageDialog ? (
        <ImageUploadDialog
          openDialog={true}
          onSubmit={handlePropertyHighlightUpload}
          onClose={handlePropertyHighlightUpload}
          isMyAccountUpload={isAValidSessionSwitch}
          imageType={"propertyHighlight"}
          listgenUserID={userData && userData.listgenUserID} //not required  because designation Logo doesnt required any image upload
          currentImage={uploadCurrentImage}
          teamMode={false}
        />
      ) : (
        ""
      )}

      <DocumentTitle title="Property Highlights">
        <DashboardLayout title="" isCompleteInvisible={true}>
          {showErrorModal ? (
            <ErrorModal
              errorModalText={errorModalText}
              handleErrorModal={handleErrorModal}
              maxPropertiesNum={maxPropertiesNum}
              missingFields={missingFields}
            />
          ) : (
            ""
          )}
          {showBuyerPermissionModal ? (
            <BuyerPermissionModal
              propertyAddress={
                pendingBuyerProperty?.bannerText1 ||
                pendingBuyerProperty?.address ||
                ""
              }
              onConfirm={handleBuyerPermissionConfirm}
              onCancel={handleBuyerPermissionCancel}
            />
          ) : (
            ""
          )}
          {showAddPropDialog ? (
            <PropHighlightsAddProperty
              onCloseFn={handleFormClose}
              openUploadMedia={openUploadMedia}
              handleInputChange={handleInputChange}
              editPropFormData={editPropFormData}
              listgenUserID={userData && userData.listgenUserID}
              handleAddProperty={handleAddProperty}
              userData={userData}
              agentData={userData}
              showAllPropertiesButton={showAllPropertiesButton}
              showButton={showButton}
            />
          ) : (
            ""
          )}
          {showEditDialog ? (
            <PropHighlightsEditProperty
              onCloseFn={handleFormClose}
              openUploadMedia={openUploadMedia}
              handleInputChange={handleInputChange}
              editPropFormData={editPropFormData}
              listgenUserID={userData && userData.listgenUserID}
              onEditFormSubmit={onEditFormSubmit}
              userData={userData}
              agentData={userData}
              oldPropData={oldPropData}
              areaID={areaID}
              onReplaceProperty={handleReplaceProperty}
              showAllPropertiesButton={showAllPropertiesButton}
              showButton={showButton}
            />
          ) : (
            ""
          )}
          {showHighlightTitleDialog ? (
            <HighlightTitleDialog
              propertyHighlightsTitle={propertyHighlightsTitle}
              propertyHighlightsSubtitle={propertyHighlightsSubtitle}
              handleTitleChange={handleTitleChange}
              onHighlightTitleClose={onHighlightTitleClose}
              oldTitle={oldTitle}
              maxHighlightTitleLength={maxHighlightTitleLength}
              maxHighlightSubtitleLength={maxHighlightSubtitleLength}
              handleAddTitle={handleAddTitle}
              showAllPropertiesButton={showAllPropertiesButton}
            />
          ) : (
            ""
          )}
          {showHighlightFooterDialog ? (
            <HighlightFooterDialog
              propertyHighlightsFooterText1={propertyHighlightsFooterText1}
              propertyHighlightsFooterText2={propertyHighlightsFooterText2}
              handleFooterChange={handleFooterChange}
              onHighlightFooterClose={onHighlightFooterClose}
              oldFooter={oldFooter}
              maxHighlightFooter1Length={maxHighlightFooter1Length}
              maxHighlightFooter2Length={maxHighlightFooter2Length}
              handleAddFooter={handleAddFooter}
              showAllPropertiesButton={showAllPropertiesButton}
            />
          ) : (
            ""
          )}
          <Portlet className={classes.contentPadded}>
            <PortletContent noPadding className="portlet-content">
              <div className={classes.propHighlightContainer}>
                <div className={classes.propHighlightTitleContainer}>
                  <Link
                    to={`/pma/${thisArea}`}
                    className="property-highlights-back-button"
                  >
                    <p>Back to PMA</p>
                  </Link>
                  {/* Show change indicator if properties have been modified */}
                  {hasSelectedPropertiesChanged() && (
                    <div
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        padding: "8px 12px",
                        marginTop: "10px",
                        marginBottom: "10px",
                        textAlign: "center",
                        color: "red",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      ⚠️ You have unsaved changes to your property selections
                    </div>
                  )}
                  <div className={classNames(classes.propHighlightTitle)}>
                    Showcase Your Properties!
                  </div>
                  <div className={classes.highlightTitleDivider}></div>
                  <div className={classes.highlightSubtitle}>
                    Select up to {maxPropertiesNum} Properties from the list
                    below that you would like to feature
                  </div>
                </div>
              </div>
              {/*<div
                style={{ fontSize: "50px" }}
                onClick={() => resetDisplayOrder()}
              >
                Reset displayOrder
              </div>*/}

              {isLoading ? (
                <div className="prop-highlight-table-empty-container">
                  <p>Loading Properties ....</p>
                </div>
              ) : (
                <div className="prop-highlight-table-container">
                  {/*<div className="unique-btn" onClick={() => getUniqueProps()}>
                  <p>UNIQUE</p>
                </div>*/}
                  <div className="property-highlights-header-container">
                    <div className="property-highlights-footer-container">
                      {isBtnLoading ? (
                        <div
                          className={classNames(
                            classes.progressWrapper,
                            classes.displyFlex,
                            classes.centerJustify,
                          )}
                        >
                          <CircularProgress />
                        </div>
                      ) : showAllPropertiesButton ? (
                        <div>
                          <button
                            type="button"
                            className="property-highlights-submit-button"
                            onClick={() => wholePageSubmit(false)}
                            style={{
                              animation:
                                hasSelectedPropertiesChanged() &&
                                !hasClickedSaveOrBack
                                  ? "flash-save-button 1.5s infinite"
                                  : "none",
                            }}
                          >
                            <p>Save for All Areas</p>
                          </button>
                        </div>
                      ) : (
                        ""
                      )}
                      {showButton && (
                        <button
                          type="button"
                          className="property-highlights-submit-button"
                          onClick={() => {
                            console.log(
                              "Save for This Area Only button clicked",
                            );
                            wholePageSubmit(true);
                          }}
                          style={{
                            animation:
                              hasSelectedPropertiesChanged() &&
                              !hasClickedSaveOrBack
                                ? "flash-save-button 1.5s infinite"
                                : "none",
                          }}
                        >
                          <p>Save for This Area Only</p>
                        </button>
                      )}
                    </div>

                    {/*    <div
                      className="property-highlights-edit-button"
                      onClick={scrollDownToProperties}
                    >
                      <p>Edit My {maxPropertiesNum} Sold Properties</p>
                    </div> */}
                    {/*{showAllPropertiesButton && (
                      <div
                        className="property-highlights-submit-button"
                        onClick={() => wholePageSubmit(false)}
                      >
                        <p>Save For All Areas</p>
                      </div>
                    )}
                    {showButton && (
                      <div
                        className="property-highlights-submit-button"
                        onClick={() => wholePageSubmit(true)}
                      >
                        <p>Save for This Area Only</p>
                      </div>
                    )}*/}
                  </div>

                  {(agentSelectedPhotoDetails || []).length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      className="drag-drop-context"
                    >
                      <SortableContext
                        items={(agentSelectedPhotoDetails || []).map(
                          (prop) =>
                            prop.dragDropId ||
                            `${prop.streetNumber || ""}-${
                              prop.streetName || ""
                            }-${prop.unitNumber || "-"}-${
                              prop.photoURL || prop.optimizedPhotoURL || ""
                            }-${prop.listingID || ""}`.replace(/\s+/g, "-"),
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <YETwoPropertyHighlights
                          currentSoldListings={agentSelectedPhotoDetails || []}
                          mlsAreaID={areaID}
                          isAgentPage={true}
                          showAllEditButtons={showButton}
                          setHighlightTitleDialog={setHighlightTitleDialog}
                          setHighlightFooterDialog={setHighlightFooterDialog}
                          onHighlightTitleOpen={onHighlightTitleOpen}
                          onHighlightFooterOpen={onHighlightFooterOpen}
                          title={propertyHighlightsTitle}
                          subtitle={propertyHighlightsSubtitle}
                          agentData={userData}
                          footerText1={propertyHighlightsFooterText1}
                          footerText2={propertyHighlightsFooterText2}
                          openEditDialogFn={handleOpenEditDialog}
                          isDraggable={true}
                          activeId={activeId}
                          SortablePhotoWrapper={SortablePhotoWrapper}
                          handleSelect={handleSelect}
                        />
                      </SortableContext>
                    </DndContext>
                  )}
                  <div className="prop-highlight-table">
                    <div className="prop-highlight-thead">
                      <div>
                        <p>Order</p>
                      </div>
                      <div>
                        <p>Select</p>
                        <p>Property</p>
                      </div>
                      <div>
                        <p>Address</p>
                      </div>
                      <div>
                        <p>Status and Price</p>
                      </div>
                      <div>
                        <p>Represented</p>
                      </div>
                    </div>

                    {(agentSelectedPhotoDetails || []).length > 0 && (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        className="drag-drop-context"
                      >
                        <SortableContext
                          items={(agentSelectedPhotoDetails || []).map(
                            (prop) =>
                              prop.dragDropId ||
                              `${prop.streetNumber || ""}-${
                                prop.streetName || ""
                              }-${prop.unitNumber || "-"}-${
                                prop.photoURL || prop.optimizedPhotoURL || ""
                              }-${prop.listingID || ""}`.replace(/\s+/g, "-"),
                          )}
                          strategy={verticalListSortingStrategy}
                        >
                          <div
                            className="draggable-div"
                            style={getPHListStyle(activeId !== null)}
                          >
                            {(agentSelectedPhotoDetails || []).map(
                              (prop, index) => {
                                return (
                                  <SortableItem
                                    key={
                                      prop.dragDropId ||
                                      `${prop.streetNumber || ""}-${
                                        prop.streetName || ""
                                      }-${prop.unitNumber || "-"}-${
                                        prop.photoURL ||
                                        prop.optimizedPhotoURL ||
                                        ""
                                      }-${prop.listingID || index}`
                                    }
                                    prop={prop}
                                    index={index}
                                    areaID={areaID}
                                    handleSelect={handleSelect}
                                    openEditDialogFn={handleOpenEditDialog}
                                    showButton={showButton}
                                  />
                                );
                              },
                            )}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>

                  <button
                    type="button"
                    className="add-property-row"
                    onClick={() => handleAddPropDialog(true)}
                  >
                    <div className="add-property-text">
                      <div className="add-property-button">
                        <div>+</div>
                      </div>
                      <div>CLICK HERE TO ADD A NEW PROPERTY MANUALLY</div>
                    </div>
                  </button>

                  <div className="prop-highlight-table-title-container">
                    <div className="prop-highlight-table-title-text">
                      YOUR MOST RECENT SALES
                    </div>
                    <div className="prop-highlight-table-subtitle-text flash-text">
                      <p>
                        Select the {maxPropertiesNum} properties you would like
                        to feature.
                      </p>
                      <p>
                        Using the table above, you can drag and drop your
                        properties into any order.
                      </p>
                    </div>
                  </div>

                  {/* Unselected Properties Table */}
                  {unselectedPhotoDetails.length > 0 && (
                    <div className="prop-highlight-table unselected-table">
                      <div className="prop-highlight-thead">
                        <div>
                          <p>Status</p>
                        </div>
                        <div>
                          <p>Select</p>
                          <p>Property</p>
                        </div>
                        <div>
                          <p>Address</p>
                        </div>
                        <div>
                          <p>Status and Price</p>
                        </div>
                        <div>
                          <p>Represented</p>
                        </div>
                      </div>
                      {unselectedPhotoDetails.map((prop, index) => (
                        <div
                          className="highlight-row-container"
                          key={`unselected-${prop.streetNumber}-${prop.streetName}-${prop.unitNumber}-${prop.photoURL}`}
                        >
                          <HighlightRow
                            prop={prop}
                            index={index}
                            areaID={areaID}
                            handleSelect={() => handleSelect(prop, true)}
                            openEditDialogFn={handleOpenEditDialog}
                            isSelectedTable={false}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </PortletContent>
          </Portlet>
        </DashboardLayout>
      </DocumentTitle>
    </Fragment>
  );
};

PropertyHighlights.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default compose(
  withSnackbar,
  withStyles(
    (theme) => ({
      ...styles(theme),
      ...styles2(theme),
    }),
    {
      withTheme: true,
    },
  ),
)(PropertyHighlights);
